import { useEffect, useState, type ReactNode } from 'react';
import SheetDialog from './SheetDialog';

interface Props {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
  /** When set, the confirm button stays disabled until the user types this
   *  exact text (the client-name confirmation on signed-state danger flows). */
  requireTypedConfirmation?: string;
  busy?: boolean;
  errorMessage?: string | null;
  /** Receives the typed confirmation text (when `requireTypedConfirmation`
   *  is set) so callers that need the value, like reset-signature, don't
   *  have to duplicate this modal. */
  onConfirm: (typed?: string) => void;
  onCancel: () => void;
}

/** A single reusable confirm dialog: plain one-step confirms (publish,
 *  archive, reload-after-stale) and the loud typed-confirmation flows
 *  (override edit, reset signature) both go through this. */
export default function ConfirmModal({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'default',
  requireTypedConfirmation,
  busy = false,
  errorMessage,
  onConfirm,
  onCancel,
}: Props) {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const locked = Boolean(requireTypedConfirmation) && typed !== requireTypedConfirmation;
  const danger = tone === 'danger';

  return (
    <SheetDialog open={open} title={title} onClose={onCancel} busy={busy}>
      <div>
        <div className="mt-3 text-sm leading-relaxed text-ink-soft">{message}</div>

        {requireTypedConfirmation && (
          <label className="mt-5 block space-y-2 text-sm font-medium text-ink">
            <span>
              Type <span className="font-mono text-ink-soft">{requireTypedConfirmation}</span> to confirm
            </span>
            <input
              className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-accent focus:ring-2 focus:ring-accent/15"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoFocus
              disabled={busy}
            />
          </label>
        )}

        {errorMessage && <p className="mt-4 text-sm text-error">{errorMessage}</p>}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            className="rounded-full border border-line px-4 py-2 text-sm font-medium text-ink-soft transition hover:border-ink-faint disabled:opacity-50"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={
              'rounded-full px-4 py-2 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-40 ' +
              (danger ? 'bg-error hover:bg-[#832f24]' : 'bg-accent hover:bg-accent-deep')
            }
            onClick={() => onConfirm(requireTypedConfirmation ? typed : undefined)}
            disabled={busy || locked}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </SheetDialog>
  );
}
